import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdNotificationsNone, MdNotificationsActive } from "react-icons/md";

import { useNotifications } from "../notifications/NotificationsContext";
import { fetchNotifications, typeLabel } from "../notifications/api";
import { timeAgo } from "../notifications/format";
import "../notifications.css";

/* ------------------------------------------------------------------ *
 * Top-bar bell: badge from the NotificationsProvider poll, and a
 * dropdown of the newest 20 items (viewed ones muted). Clicking an item
 * marks it viewed for the whole business and follows its subject.
 * ------------------------------------------------------------------ */

const PANEL_LIMIT = 20;

/* One row — the dropdown and the feed page share it. `extra` renders to
   the right (feed-page actions). */
export function NotificationRow({ item, onOpen, extra, showMeta = false }) {
  const viewed = !!item.viewed;
  return (
    <div
      className={`nf-item${viewed ? " is-viewed" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen && onOpen(item);
        }
      }}
    >
      <span className="nf-item-dot" aria-hidden="true" />
      <div className="nf-item-body">
        <div className="nf-item-top">
          <span className="nf-item-title">{item.title || typeLabel(item.type)}</span>
          <span className="nf-item-time" title={item.created_at}>
            {timeAgo(item.created_at)}
          </span>
        </div>
        {item.message && <div className="nf-item-msg">{item.message}</div>}
        {showMeta && (
          <div className="nf-item-meta">
            <span className="nf-type">{typeLabel(item.type)}</span>
            {viewed && item.viewed_by && (item.viewed_by.full_name || item.viewed_by.userName) && (
              <span>
                Read by {item.viewed_by.full_name || item.viewed_by.userName}
                {item.viewed_at ? ` · ${timeAgo(item.viewed_at)}` : ""}
              </span>
            )}
          </div>
        )}
      </div>
      {extra && (
        <div className="nf-item-actions" onClick={(e) => e.stopPropagation()}>
          {extra}
        </div>
      )}
    </div>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const notif = useNotifications();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ring, setRing] = useState(false);
  const wrapRef = useRef(null);
  const prevCount = useRef(0);

  const unviewed = (notif && notif.unviewedCount) || 0;
  const version = notif ? notif.version : 0;
  const businessId = notif ? notif.businessId : null;

  // A rising count wiggles the bell once.
  useEffect(() => {
    if (unviewed > prevCount.current) {
      setRing(true);
      const t = setTimeout(() => setRing(false), 1000);
      prevCount.current = unviewed;
      return () => clearTimeout(t);
    }
    prevCount.current = unviewed;
    return undefined;
  }, [unviewed]);

  const load = useCallback(() => {
    setLoading(true);
    fetchNotifications({ limit: PANEL_LIMIT })
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Fetch when opened, and again whenever viewed state changes while open
  // (or the poll finds something new — `version` is not bumped for that,
  // so also refetch when the count changes).
  useEffect(() => {
    if (open) load();
  }, [open, load, version, unviewed, businessId]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = async (item) => {
    setOpen(false);
    // Optimistic: the row goes muted right away.
    setItems((prev) => prev.map((n) => (n._id === item._id ? { ...n, viewed: true } : n)));
    const to = notif ? await notif.markViewed(item) : null;
    if (to) navigate(to, { state: { notification: item } });
  };

  const Icon = unviewed > 0 ? MdNotificationsActive : MdNotificationsNone;

  return (
    <div className="nf-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`nf-bell${open ? " is-open" : ""}${ring ? " has-new" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unviewed > 0 ? `Notifications, ${unviewed} unread` : "Notifications"
        }
        aria-expanded={open}
        title="Notifications"
      >
        <Icon />
        {unviewed > 0 && (
          <span className="nf-badge">{unviewed > 99 ? "99+" : unviewed}</span>
        )}
      </button>

      {open && (
        <div className="nf-panel" role="dialog" aria-label="Notifications">
          <div className="nf-panel-head">
            <span className="nf-panel-title">Notifications</span>
            <span className="nf-panel-count">
              {unviewed > 0 ? `${unviewed} unread` : "All caught up"}
            </span>
            <span className="rf-spacer" />
            <button
              type="button"
              className="nf-link"
              disabled={unviewed === 0}
              onClick={() => notif && notif.markAll()}
            >
              Mark all as read
            </button>
          </div>
          <div className="nf-panel-list">
            {loading && items.length === 0 ? (
              <div className="nf-empty">Loading…</div>
            ) : items.length === 0 ? (
              <div className="nf-empty">Nothing here yet.</div>
            ) : (
              items.map((item) => (
                <NotificationRow key={item._id} item={item} onOpen={openItem} />
              ))
            )}
          </div>
          <div className="nf-panel-foot">
            <button
              type="button"
              className="nf-link"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
            >
              See all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
